import Avatar from '@mui/material/Avatar';
import { SxProps, Theme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

/** Initials from the first two words, e.g. "Jane Doe" → "JD". */
export const initialsOf = (name?: string | null): string =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export interface AvatarDisplayProps {
  src?: string | null;
  /** Used for the initials fallback and image alt text. */
  name?: string | null;
  /** Avatar diameter in pixels. */
  size?: number;
  variant?: 'circular' | 'rounded' | 'square';
  /** Icon shown when there is no image and no usable initials (e.g. 'solar:buildings-bold'). */
  fallbackIcon?: string;
  sx?: SxProps<Theme>;
}

/**
 * Pure avatar with an initials/icon placeholder (JT1-1539). Deliberately hook-free so it can render
 * in list rows without a Redux/notistack provider; the editable variant wraps it with the camera flow.
 */
export default function AvatarDisplay({
  src,
  name,
  size = 40,
  variant = 'circular',
  fallbackIcon,
  sx,
}: AvatarDisplayProps) {
  const initials = initialsOf(name);

  return (
    <Avatar
      src={src ?? undefined}
      alt={name ?? undefined}
      variant={variant}
      sx={[{ width: size, height: size, fontSize: size * 0.4 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {initials || (fallbackIcon ? <Iconify icon={fallbackIcon} width={size * 0.5} /> : null)}
    </Avatar>
  );
}
